import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Check, Share } from 'lucide-react'

interface ShareButtonProps {
    title: string
}

export const ShareButton: FC<ShareButtonProps> = ({ title }): ReactNode => {
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!copied) return

        const timer = window.setTimeout(() => setCopied(false), 2000)

        return () => window.clearTimeout(timer)
    }, [copied])

    const handleShare = async () => {
        const url = window.location.href

        if (navigator.share) {
            try {
                await navigator.share({ title, url })

                return
            }
            catch {
                // The visitor closed the share sheet, or sharing is blocked. Fall back to copying.
            }
        }

        try {
            await navigator.clipboard.writeText(url)
            setCopied(true)
        }
        catch {
            window.prompt('Copy this link', url)
        }
    }

    return (
        <button
            type='button'
            onClick={() => void handleShare()}
            className='flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold underline-offset-4 transition hover:bg-muted hover:underline'
        >
            {copied ? <Check className='size-4' aria-hidden='true' /> : <Share className='size-4' aria-hidden='true' />}
            <span aria-live='polite'>{copied ? 'Link copied' : 'Share'}</span>
        </button>
    )
}
